/**
 * Consumption reminders — "quick checks".
 *
 * The backend picks one to three stock rows a household has probably already
 * used up and asks about each one; the client renders the questions and posts
 * the answers. Nothing here decides *what* gets asked — see the backend feature
 * doc `.claude/docs/features/consumption-reminders.md` for the selection model.
 */

/** The four answers a check accepts, in the order the UI offers them. */
export type CheckResponse = 'used_up' | 'still_have' | 'partial' | 'not_now';

export type CheckState = 'open' | 'answered' | 'expired';

/**
 * The stock row a check asks about. Null on a `used_up` response: answering
 * that removes the row, and the server returns no stock with the answer.
 */
export interface QuickCheckStock {
  id: number;
  productId: number;
  productName: string;
  thumbImageUrl?: string;
  /** Remaining quantity in `unit`; undefined when the server didn't send one. */
  quantityLeft?: number;
  /** Quantity the item started with, for the "some left" slider's upper bound. */
  quantityFull?: number;
  unit?: string;
  storeName?: string;
  addedAt?: Date;
  isOpen?: boolean;
  /**
   * Everything the household has of this product, across every store and
   * including this row — the "how much else is there?" the user would
   * otherwise have to go and check before answering.
   */
  productTotal?: number;
  /** How many stock rows that total covers. 1 means this is the last one. */
  productStockCount?: number;
}

export interface QuickCheck {
  id: number;
  state: CheckState;
  askedAt: Date;
  expiresAt: Date;
  /**
   * Server-rendered question text. The API also sends `questionKey` + `params`
   * for clients with their own string catalogue; we render the server's text
   * until this app has one.
   */
  question: string;
  questionKey?: string;
  params?: Record<string, string>;
  stock: QuickCheckStock | null;
  /** Answers the server accepts for this check — the buttons we may show. */
  responses: CheckResponse[];
}

/**
 * Household-level reminder settings. A household with no stored row uses these
 * defaults, so the form always has something to show — the server treats a
 * missing row the same way.
 */
export interface ReminderSettings {
  /** IANA timezone; quiet hours and "days in stock" are evaluated in it. */
  timezone: string;
  enabled: boolean;
  /** How many days between rounds (1–30). */
  cadenceDays: number;
  /** How many questions one round asks (1–3). */
  itemsPerRound: number;
  /** Hour of day (0–23) the quiet window opens and closes. */
  quietHoursStart: number;
  quietHoursEnd: number;
}

export const DEFAULT_REMINDER_SETTINGS: ReminderSettings = {
  timezone: 'Europe/Zurich',
  enabled: true,
  cadenceDays: 3,
  itemsPerRound: 2,
  quietHoursStart: 8,
  quietHoursEnd: 21,
};

/** Result of answering, as the list needs it. */
export interface RespondResult {
  checkId: number;
  response: CheckResponse;
  /** The updated stock row, or null when the answer removed it. */
  stock: QuickCheckStock | null;
}

/**
 * A row in the check list. Answered rows stay in place for the session as a
 * settled strip, so the list never silently shortens under the pointer.
 */
export type QuickCheckRowStatus = 'open' | 'saving' | 'settled';

export interface QuickCheckRow {
  check: QuickCheck;
  status: QuickCheckRowStatus;
  /** Present when settled: what the answer changed, in the user's words. */
  settledText?: string;
}

/** Whole days the item has been in stock, or null when the date is unknown. */
export function daysInStock(stock: QuickCheckStock | null, now: Date = new Date()): number | null {
  if (!stock?.addedAt) return null;
  const ms = now.getTime() - stock.addedAt.getTime();
  if (!isFinite(ms)) return null;
  return Math.max(0, Math.floor(ms / 86_400_000));
}

/** Whole days until the check expires, floored at 0. */
export function daysUntil(when: Date | undefined, now: Date = new Date()): number | null {
  if (!when) return null;
  const ms = when.getTime() - now.getTime();
  if (!isFinite(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

/** "1 piece" / "0.4 of 1 L" — the quantity line under a product name. */
export function quantityText(stock: QuickCheckStock | null): string {
  if (!stock) return '';
  const unit = stock.unit ? ` ${stock.unit}` : '';
  const left = stock.quantityLeft;
  const full = stock.quantityFull;
  if (left == null) return full == null ? '' : `${trimNumber(full)}${unit}`;
  if (full == null || left === full) return `${trimNumber(left)}${unit}`;
  return `${trimNumber(left)} of ${trimNumber(full)}${unit}`;
}

/**
 * Units that mean "whole pieces". The check payload carries `unit` but no
 * `unit_type` (unlike the stock endpoints), so the type has to be inferred
 * from the unit itself — the iOS client infers it from the same list.
 */
const PIECE_UNITS = ['piece', 'pieces', 'pcs', 'pc', 'stk', 'stück', 'stueck', 'x', 'ea'];

export function isCountedInPieces(stock: QuickCheckStock | null): boolean {
  const unit = (stock?.unit ?? '').trim().toLowerCase();
  if (!unit) return true;
  return PIECE_UNITS.includes(unit);
}

/**
 * What the household has of this product in total, this item included:
 * "Just this one" / "3" / "2.4 L · 3 items".
 *
 * The question is "is it gone?", which is much easier to answer knowing
 * whether anything is behind it. Null when the server sent no totals, so an
 * older API simply omits the line.
 */
export function totalInStockText(stock: QuickCheckStock | null): string | null {
  if (!stock) return null;
  const count = stock.productStockCount;
  const total = stock.productTotal;
  if (count == null || count <= 0 || total == null) return null;

  // Being the last one is the fact that changes the answer, so say it in
  // words rather than making the user read a 1 off a quantity.
  if (count === 1) return 'Just this one';

  // Pieces are already counted in items — "3 · 3 items" says it twice.
  if (isCountedInPieces(stock)) return trimNumber(total);

  const unit = stock.unit ? ` ${stock.unit}` : '';
  return `${trimNumber(total)}${unit} · ${count} items`;
}

/** 0.40 → "0.4", 1.00 → "1". */
export function trimNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}
